import { ComponentPropsWithoutRef, ReactNode, useMemo } from "react"
import * as React from "react"
import { CustomSelectTrigger, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"
import { Skeleton } from "@ui/shad/skeleton"
import { cn } from "@ui/utils"

export const NULL_SENTINEL_VALUE = "NULL"

interface Option {
  value: string | number
  label: string | React.ReactNode
  triggerLabel?: string
}

type OptionsInput = Option[] | readonly Option[] | undefined

interface SelectInputProps<T extends string | number | null | undefined> {
  id: string
  value?: T
  onChange?: ((value: T) => void) | ((value: T | null | undefined) => void)
  options: OptionsInput
  label?: ReactNode
  placeholder?: string
  disabled?: boolean
  error?: boolean
  className?: string
  triggerButton?: ReactNode
  contentProps?: ComponentPropsWithoutRef<typeof SelectContent>
  nullable?: boolean
  size?: "sm" | "default" | "xs"
  helperText?: string
}

export function SelectInput<T extends string | number | null | undefined>({
  id,
  value,
  onChange,
  options,
  label,
  placeholder = "Select an option",
  disabled = false,
  error,
  className,
  triggerButton,
  contentProps,
  nullable = false, // Default nullable to false
  size,
  helperText,
}: SelectInputProps<T>) {
  const selectValue = value === null || value === undefined ? NULL_SENTINEL_VALUE : value?.toString()

  const processedOptions = useMemo(() => {
    if (!options) return undefined
    if (!nullable) return options

    const hasNullOption = options.some((opt) => opt.value === NULL_SENTINEL_VALUE)
    if (hasNullOption) return options

    return [{ value: NULL_SENTINEL_VALUE, label: placeholder || "..." }, ...options]
  }, [options, nullable, placeholder])

  const selectedTriggerLabel = useMemo(() => {
    const match = processedOptions?.find((option) => option.value?.toString() === selectValue)
    return match?.triggerLabel
  }, [processedOptions, selectValue])

  return (
    <div className={cn("space-y-1", className)}>
      {label && (
        <Label htmlFor={id} className={cn(error && "text-destructive")}>
          {label}
        </Label>
      )}
      {!processedOptions ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        <Select
          value={selectValue}
          onValueChange={(val) => {
            if (onChange) {
              if (val === NULL_SENTINEL_VALUE) {
                ;(onChange as (value: T | null | undefined) => void)(null)
              } else {
                const originalTypeIsNumber = typeof value === "number"
                const converted = originalTypeIsNumber ? Number(val) : val
                ;(onChange as (value: T | null | undefined) => void)(converted as T | null)
              }
            }
          }}
          disabled={disabled}
        >
          {triggerButton ? (
            <CustomSelectTrigger asChild>{triggerButton}</CustomSelectTrigger>
          ) : (
            <SelectTrigger id={id} size={size} className={cn(error && "border-destructive")}>
              <SelectValue placeholder={placeholder}>{selectedTriggerLabel}</SelectValue>
            </SelectTrigger>
          )}
          <SelectContent {...contentProps}>
            {processedOptions.map((option) => (
              <SelectItem key={option.value} value={option.value?.toString()}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {helperText && <p className="text-muted-foreground text-xs">{helperText}</p>}
    </div>
  )
}
